import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

function FooterPage({ title, children }) {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return (
        <Wrap>
            <div className="fp-hero">
                <div className="container-fluid theme-container">
                    <nav className="fp-breadcrumb" aria-label="Breadcrumb">
                        <Link to="/" className="fp-crumb-link">
                            <i className="fa fa-home" aria-hidden="true" />
                            Home
                        </Link>
                        <span className="fp-sep" aria-hidden="true">
                            <i className="fa fa-chevron-right" />
                        </span>
                        <span className="fp-crumb-current">{title}</span>
                    </nav>
                    <div className="fp-hero-inner">
                        <span className="fp-accent" aria-hidden="true" />
                        <div>
                            <p className="fp-eyebrow">Coliza</p>
                            <h1 className="fp-title">{title}</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="fp-main">
                <div className="container-fluid theme-container">
                    <div className="fp-card-shell">
                        <div className="fp-body">{children}</div>
                    </div>
                </div>
            </div>
        </Wrap>
    );
}

export default FooterPage;

const Wrap = styled.section`
    font-family: 'Inter', 'Segoe UI', -apple-system, sans-serif;
    background: #f8fafc;
    min-height: 55vh;

    /* ── Hero band ── */
    .fp-hero {
        background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 48%, #ffffff 100%);
        border-bottom: 1px solid #e2e8f0;
        padding: 36px 0 40px;
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: -80px;
            right: -60px;
            width: 280px;
            height: 280px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%);
            pointer-events: none;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: -100px;
            left: -40px;
            width: 220px;
            height: 220px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(124, 58, 237, 0.06) 0%, transparent 70%);
            pointer-events: none;
        }
    }

    .fp-breadcrumb {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        font-size: 13px;
        margin-bottom: 22px;
        position: relative;
        z-index: 1;
    }

    .fp-crumb-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: #2563eb;
        text-decoration: none;
        font-weight: 500;
        padding: 5px 12px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid #dbeafe;
        transition: background 0.2s, border-color 0.2s, transform 0.15s;

        i { font-size: 11px; }

        &:hover {
            background: #ffffff;
            border-color: #93c5fd;
            text-decoration: none;
            transform: translateY(-1px);
        }
    }

    .fp-sep {
        color: #cbd5e1;
        font-size: 10px;
        padding: 0 2px;
    }

    .fp-crumb-current {
        color: #64748b;
        font-weight: 500;
        padding: 5px 12px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.55);
        border: 1px solid #e2e8f0;
    }

    .fp-hero-inner {
        display: flex;
        align-items: stretch;
        gap: 18px;
        position: relative;
        z-index: 1;
    }

    .fp-accent {
        width: 5px;
        border-radius: 6px;
        background: linear-gradient(180deg, #2563eb 0%, #7c3aed 100%);
        flex-shrink: 0;
        box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);
    }

    .fp-eyebrow {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #2563eb;
        margin: 0 0 6px;
    }

    .fp-title {
        font-family: 'Poppins', 'Segoe UI', sans-serif;
        font-size: clamp(1.75rem, 3.5vw, 2.35rem);
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        letter-spacing: -0.02em;
        line-height: 1.2;
    }

    /* ── Content area ── */
    .fp-main {
        padding: 0 0 80px;
        margin-top: -22px;
        position: relative;
        z-index: 2;
    }

    .fp-card-shell {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        box-shadow:
            0 1px 2px rgba(15, 23, 42, 0.04),
            0 12px 40px rgba(15, 23, 42, 0.06);
        overflow: hidden;

        &::before {
            content: '';
            display: block;
            height: 3px;
            background: linear-gradient(90deg, #2563eb, #7c3aed, #2563eb);
            background-size: 200% 100%;
        }
    }

    .fp-body {
        font-size: 15.5px;
        line-height: 1.85;
        color: #475569;
        padding: 36px 40px 44px;
        max-width: 900px;

        @media (max-width: 576px) {
            padding: 28px 22px 32px;
        }

        /* Lead paragraph */
        > p:first-child {
            font-size: 16.5px;
            color: #334155;
            line-height: 1.75;
            padding-bottom: 4px;
        }

        h2 {
            font-family: 'Poppins', 'Segoe UI', sans-serif;
            font-size: 1.2rem;
            font-weight: 600;
            color: #0f172a;
            margin: 36px 0 14px;
            padding-left: 14px;
            border-left: 3px solid #2563eb;
            letter-spacing: -0.01em;
        }

        h3 {
            font-family: 'Poppins', 'Segoe UI', sans-serif;
            font-size: 1.02rem;
            font-weight: 600;
            color: #1e293b;
            margin: 26px 0 10px;
        }

        p {
            margin: 0 0 16px;
        }

        em {
            display: block;
            margin-top: 28px;
            padding: 16px 18px;
            background: #f8fafc;
            border-left: 3px solid #cbd5e1;
            border-radius: 0 10px 10px 0;
            font-size: 14px;
            color: #64748b;
            font-style: normal;
        }

        ul, ol {
            margin: 0 0 20px;
            padding: 0;
            list-style: none;
        }

        ul li, ol li {
            position: relative;
            margin-bottom: 10px;
            padding-left: 26px;
            color: #475569;
        }

        ul li::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0.62em;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: linear-gradient(135deg, #2563eb, #7c3aed);
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        ol {
            counter-reset: fp-counter;
        }

        ol li {
            counter-increment: fp-counter;
        }

        ol li::before {
            content: counter(fp-counter);
            position: absolute;
            left: 0;
            top: 0.12em;
            width: 22px;
            height: 22px;
            line-height: 20px;
            text-align: center;
            border-radius: 6px;
            background: #eff6ff;
            color: #2563eb;
            font-size: 12px;
            font-weight: 700;
            border: 1px solid #dbeafe;
        }

        ol li {
            padding-left: 34px;
        }

        strong {
            color: #1e293b;
            font-weight: 600;
        }

        a {
            color: #2563eb;
            font-weight: 500;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: color 0.15s, border-color 0.15s;

            &:hover {
                color: #1d4ed8;
                border-bottom-color: #93c5fd;
                text-decoration: none;
            }
        }

        /* Highlight cards */
        .fp-card {
            background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
            border: 1px solid #dbeafe;
            border-radius: 14px;
            padding: 24px 28px;
            margin: 28px 0;
            box-shadow: 0 4px 20px rgba(37, 99, 235, 0.06);
            position: relative;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: linear-gradient(180deg, #2563eb, #7c3aed);
                border-radius: 4px 0 0 4px;
            }
        }

        /* Contact grid */
        .fp-contact-row {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
            margin-top: 4px;
        }

        .fp-contact-item {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 18px 20px;
            transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

            &:hover {
                border-color: #bfdbfe;
                box-shadow: 0 6px 20px rgba(37, 99, 235, 0.08);
                transform: translateY(-2px);
            }

            strong {
                display: block;
                color: #64748b;
                margin-bottom: 8px;
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                font-weight: 700;
            }

            a {
                font-size: 15px;
                word-break: break-word;
            }
        }

        /* Blog articles */
        .fp-article {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 28px 28px 24px;
            margin-bottom: 20px;
            transition: border-color 0.2s, box-shadow 0.2s;

            &:hover {
                border-color: #cbd5e1;
                box-shadow: 0 8px 28px rgba(15, 23, 42, 0.05);
            }

            &:last-child {
                margin-bottom: 0;
            }

            h2 {
                margin-top: 0;
                border-left: none;
                padding-left: 0;
                font-size: 1.25rem;
            }
        }

        .fp-article-meta {
            display: inline-block;
            font-size: 12px;
            font-weight: 600;
            color: #2563eb;
            background: #eff6ff;
            border: 1px solid #dbeafe;
            padding: 4px 12px;
            border-radius: 999px;
            margin-bottom: 14px;
            letter-spacing: 0.02em;
        }

        /* Sitemap */
        .fp-sitemap-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
            gap: 18px;
            margin-top: 12px;
        }

        .fp-sitemap-col {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 20px 22px;
            transition: border-color 0.2s, box-shadow 0.2s;

            &:hover {
                border-color: #bfdbfe;
                box-shadow: 0 6px 24px rgba(15, 23, 42, 0.04);
            }

            h3 {
                margin: 0 0 14px;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.1em;
                color: #2563eb;
                font-weight: 700;
                padding-bottom: 10px;
                border-bottom: 2px solid #dbeafe;
            }

            ul {
                margin: 0;
            }

            li {
                margin-bottom: 0;
                padding-left: 0;

                &::before {
                    display: none;
                }
            }

            a {
                display: block;
                padding: 7px 10px;
                margin: 0 -10px;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                color: #475569;
                border-bottom: none;
                transition: background 0.15s, color 0.15s, padding-left 0.15s;

                &:hover {
                    background: #ffffff;
                    color: #2563eb;
                    padding-left: 14px;
                    border-bottom: none;
                }
            }
        }
    }
`;
